import 'element-internals-polyfill';
import { Component, h, Element, Method, Prop, Host, State, AttachInternals, Listen, Watch } from '@stencil/core';
import { selectFormSubmitters } from '../../utils/utils';
import '@material/web/list/list';

/** OmkList is a container for list items. It has an unnamed slot for content which should be list-items. It also has a named "header" slot for the omk-header component if needed. */
@Component({
  tag: 'omk-list',
  styleUrl: 'omk-list.scss',
  shadow: { delegatesFocus: true },
  formAssociated: true,
})
export class OmkList {
  /** If true, the list occupies the full width of it's container. When this is false, the inset list is displayed. */
  @Prop() fullWidth: boolean = false;
  /** If set, the validation message to be displayed at the bottom of the list. */
  @Prop() customValidationMessage: string = '';
  /** @internal Do not set. Forms check this property before submit and when checkValidity() is called. */
  @Prop({ mutable: true }) validity: ValidityState;
  /** @internal Do not set - regard as read-only. The current validation message whether custom or built-in. */
  @Prop({ mutable: true }) validationMessage: string;
  /** Show validation messages immediately instead of waiting for user interaction or form submission. */
  @Prop() alwaysShowValidationMessage: boolean;
  /** Hides the inline validation messages. Used when error messages are to be displayed using other means other than the inline capability. */
  @Prop() hideValidationMessage: boolean;
  /** Stops validation of components slotted in the start slot of the omk-list-item.
   * By default, if no custom validation message is provided, the omk-list interrogates components slotted (start slot) into each omk-list-item.
   * If the slotted component follows our guidelines for form components, the validity states and validation messages are retrieved from these and the first error message from these components is displayed as the list's validation message.
   * This prop stops the validation of slotted components behavior. */
  @Prop() optOutOfSlottedComponentValidation: boolean = false;
  /** Applies a border around the list area. NB: Only applies when the full-width attribute is false. */
  @Prop({ reflect: true }) outlined: boolean = false;
  /** @internal Do not set - regard as read-only. Used to stop the OmkForm from trying to focus on this element as it is merely a container for actual form elements. */
  @Prop({ mutable: true }) focusable: boolean = false;

  @State() isGroupInvalid: boolean = false;
  @State() touched: boolean = false;
  @State() validateAfterNextRender = false;

  @Element() el: HTMLElement;

  @AttachInternals() internals: ElementInternals;

  get mdList() {
    return this.el.shadowRoot.querySelector('md-list');
  }

  /** Retrieves the list items in the list */
  @Method()
  async getItems(): Promise<Element[]> {
    return Array.from(
      (this.mdList.lastElementChild as HTMLSlotElement).assignedElements()?.filter(t => t.tagName === 'OMK-LIST-ITEM'),
    );
  }

  @Listen('change')
  @Listen('input')
  @Listen('focusout')
  @Listen('blur')
  handleUserInput() {
    this.touched = true;
    this.validate();
  }

  @Listen('focusin')
  async handleFocusChange() {
    const listItems = await this.getItems();
    const listItemHasFocus = listItems.includes(document.activeElement);

    if (!listItemHasFocus) return;

    const otherListItems = listItems.filter(li => li !== document.activeElement);
    otherListItems.forEach(item => {
      (item as HTMLElement).tabIndex = -1;
    });
  }

  @Listen('keydown')
  async handleKeyboardSelection(ev: KeyboardEvent) {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(ev.key)) return;

    const listItems = await this.getItems();
    const listItemHasFocus: boolean =
      listItems.includes(document.activeElement as Element) ||
      listItems.includes((document.activeElement as Element).parentElement as Element);

    if (!listItemHasFocus) return;

    const activeItemIndex =
      listItems.findIndex(li => li === document.activeElement) === -1
        ? listItems.findIndex(li => li === (document.activeElement as HTMLElement).parentElement)
        : listItems.findIndex(li => li === document.activeElement);

    if (['ArrowRight', 'ArrowDown'].includes(ev.key)) {
      (listItems[activeItemIndex === listItems.length - 1 ? 0 : activeItemIndex + 1] as HTMLElement).focus();
    } else if (['ArrowLeft', 'ArrowUp'].includes(ev.key)) {
      (listItems[activeItemIndex === 0 ? listItems.length - 1 : activeItemIndex - 1] as HTMLElement).focus();
    }

    ev.preventDefault();
    this.validate();
  }

  @Watch('customValidationMessage')
  validationPropsChanged() {
    this.validateAfterNextRender = true;
  }

  async componentDidLoad() {
    if (this.alwaysShowValidationMessage) {
      this.touched = true;
    }

    if (this.customValidationMessage) this.validateAfterNextRender = true;
  }

  componentDidRender(): void {
    if (this.validateAfterNextRender) {
      this.validateAfterNextRender = false;
      this.validate();
    }
  }

  async getSlottedComponents(): Promise<Element[]> {
    return Array.from(
      (this.mdList.lastElementChild as HTMLSlotElement).assignedElements()?.filter(t => t.tagName === 'OMK-LIST-ITEM'),
    )
      .map(l => l.shadowRoot.querySelector('md-list-item'))
      .map(c => c.children.namedItem('start'));
  }

  /** @internal */
  async validateGroup(setTouched: boolean): Promise<string[]> {
    const results: string[] = [];
    const list = await this.getSlottedComponents();
    const components = list.map(e => (e as HTMLSlotElement).assignedElements()[0]);

    components.forEach(elem => {
      try {
        (elem as any).validate(setTouched);
      } catch (err) {
        return;
      }
      results.push((elem as any).validationMessage);
    });

    return results;
  }

  /** Run validation for the list with a boolean parameter to set it as touched.
   * If `setTouched` is not provided or set to false, the touched state will not be changed.
   * Validation messages are not shown until the element is touched.
   * Custom validation messages can be set through the customValidationMessage property, and don't require this method to be called afterwards.
   * Returns true if valid or disabled, false if invalid. */
  @Method()
  async validate(setTouched?: boolean): Promise<boolean> {
    if (setTouched) {
      this.touched = true;
    }

    if (this.customValidationMessage) {
      this.internals.setValidity(
        { customError: true },
        this.customValidationMessage,
        this.el.shadowRoot.querySelector('md-list'),
      );
      this.validationMessage = this.customValidationMessage;
    } else if (!this.optOutOfSlottedComponentValidation) {
      // Determine the validity of the group and the error message if there is one
      const validationMessages: string[] = await this.validateGroup(setTouched);
      const groupIsInvalid = validationMessages.some(msg => msg && msg.length > 0);

      this.internals.setValidity(
        { customError: groupIsInvalid },
        groupIsInvalid ? validationMessages.find(msg => msg.length > 0) : '',
        this.el.shadowRoot.querySelector('md-list'),
      );
    }
    this.validity = this.internals?.validity;

    // Force a re-render by setting a new reference
    this.validationMessage = this.internals?.validationMessage;
    return this.validity?.valid;
  }

  /** Reset the list to its default value. */
  @Method()
  async reset() {
    const items: Element[] = await this.getItems();
    const resetPromises = items.map((item: Element) => {
      const radio = item.querySelector('omk-radio') as HTMLOmkRadioElement;
      if (radio) {
        return (radio as any).reset();
      }
      return Promise.resolve();
    });

    await Promise.all(resetPromises);

    this.touched = false;
    this.validateAfterNextRender = false;
  }

  /** Focus on the first slotted element. */
  @Method()
  async customFocus() {
    const list = await this.getSlottedComponents();
    const components = list.map(e => (e as HTMLSlotElement).assignedElements()[0]);
    if (components.length >= 0) (components[0] as HTMLElement).focus();
  }

  render() {
    return (
      <Host class={{ 'full-width': this.fullWidth }}>
        <div class={{ 'full-width': this.fullWidth, 'container': true }}>
          <slot slot="header" name="header"></slot>
          <div class="list-area">
            <md-list class={{ outlined: this.outlined }}>
              <slot></slot>
            </md-list>
          </div>
        </div>
        {this.validationMessage && (
          <div class="validation-area">
            <omk-icon outline-variant>error</omk-icon> {this.validationMessage}
          </div>
        )}
      </Host>
    );
  }

  formAssociatedCallback(form) {
    form.addEventListener('reset', () => this.reset());
    selectFormSubmitters(form).forEach(submitter =>
      submitter.addEventListener('click', () => {
        this.touched = true;
        // re-validate just to be sure. libraryComponentOptions.componentValidationMessageOverrides might have changed.
        this.validateAfterNextRender = true;
      }),
    );
  }
}
